import { useState, useEffect } from 'react';
import FiltroUsuarios from './FiltroUsuarios';
import { useFiltroUsuarios } from '../filtroUsuarios';
import { getUsuarioActual } from '../apiAuth';

const API = '/api';

const MESES = ['Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio', 'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'];

function anosDisponibles() {
  const actual = new Date().getFullYear();
  const anos = [];
  for (let a = actual; a >= actual - 5; a--) anos.push(a);
  return anos;
}

export default function ReportesUso() {
  const hoy = new Date();
  const [modo, setModo] = useState('anio');
  const [anio, setAnio] = useState(hoy.getFullYear());
  const [mes, setMes] = useState(hoy.getMonth() + 1);
  const [usuarioId, setUsuarioId] = useState('todos');
  const [usuarios, setUsuarios] = useState([]);
  const [filas, setFilas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Administrador Plus: en vez del selector de un usuario (que lista a todos),
  // usa <FiltroUsuarios> con solo sus usuarios asignados; el backend acota igual.
  const esAdminPlus = getUsuarioActual()?.rol === 'administrador_plus';
  const filtroUsuariosKey = useFiltroUsuarios().join(',');

  useEffect(() => {
    if (esAdminPlus) return;
    fetch(`${API}/usuarios`)
      .then(r => r.json())
      .then(data => setUsuarios(Array.isArray(data) ? data : []))
      .catch(() => setUsuarios([]));
  }, []);

  useEffect(() => {
    const cargar = async () => {
      setLoading(true);
      setError('');
      try {
        const params = new URLSearchParams({ modo, anio, usuario_id: usuarioId });
        if (modo === 'mes') params.set('mes', mes);
        const r = await fetch(`${API}/reportes-uso?${params.toString()}`);
        const data = await r.json();
        if (!r.ok) {
          setError(data.error || 'Error al cargar el reporte');
          setFilas([]);
          return;
        }
        setFilas(data);
      } catch {
        setError('Error de conexión con el servidor');
        setFilas([]);
      } finally {
        setLoading(false);
      }
    };
    cargar();
  }, [modo, anio, mes, usuarioId, filtroUsuariosKey]);

  const label = { display: 'block', marginBottom: 4, fontSize: 13, color: '#4b5563' };
  const select = { padding: 8, border: '1px solid #d1d5db', borderRadius: 4, minWidth: 140 };

  const totales = filas.reduce((acc, f) => ({
    altas_prospecto: acc.altas_prospecto + f.altas_prospecto,
    altas_infonavit: acc.altas_infonavit + f.altas_infonavit,
  }), { altas_prospecto: 0, altas_infonavit: 0 });

  return (
    <div style={{ padding: 24 }}>
      <h2 style={{ color: '#1a3a6e', marginBottom: 16 }}>Reportes de Uso</h2>

      <div style={{ display: 'flex', gap: 16, marginBottom: 20, background: 'white', padding: 16, borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', flexWrap: 'wrap' }}>
        <div>
          <label style={label}>Ver por</label>
          <select style={select} value={modo} onChange={e => setModo(e.target.value)}>
            <option value="anio">Año (detalle mensual)</option>
            <option value="mes">Mes (detalle semanal)</option>
          </select>
        </div>
        <div>
          <label style={label}>Año</label>
          <select style={select} value={anio} onChange={e => setAnio(Number(e.target.value))}>
            {anosDisponibles().map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
        {modo === 'mes' && (
          <div>
            <label style={label}>Mes</label>
            <select style={select} value={mes} onChange={e => setMes(Number(e.target.value))}>
              {MESES.map((m, i) => <option key={m} value={i + 1}>{m}</option>)}
            </select>
          </div>
        )}
        {esAdminPlus ? (
          <FiltroUsuarios ancho={260} />
        ) : (
          <div>
            <label style={label}>Usuario</label>
            <select style={select} value={usuarioId} onChange={e => setUsuarioId(e.target.value)}>
              <option value="todos">Todos</option>
              {usuarios.map(u => (
                <option key={u.id} value={u.id}>{u.nombre_completo || u.usuario}</option>
              ))}
            </select>
          </div>
        )}
      </div>

      {error && <div style={{ color: '#dc2626', marginBottom: 12 }}>{error}</div>}

      <div style={{ background: 'white', borderRadius: 8, boxShadow: '0 2px 8px rgba(0,0,0,0.1)', overflowX: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 14 }}>
          <thead>
            <tr style={{ background: '#f3f4f6', textAlign: 'left' }}>
              <th style={{ padding: 10 }}>Usuario</th>
              <th style={{ padding: 10 }}>Periodo</th>
              <th style={{ padding: 10, textAlign: 'right' }}>Altas Prospectos</th>
              <th style={{ padding: 10, textAlign: 'right' }}>Altas Infonavit</th>
            </tr>
          </thead>
          <tbody>
            {loading && (
              <tr><td colSpan={4} style={{ padding: 16, textAlign: 'center', color: '#6b7280' }}>Cargando...</td></tr>
            )}
            {!loading && filas.length === 0 && (
              <tr><td colSpan={4} style={{ padding: 16, textAlign: 'center', color: '#6b7280' }}>Sin datos para el periodo seleccionado</td></tr>
            )}
            {!loading && filas.map((f, i) => (
              <tr key={`${f.usuario_id}-${f.periodo_orden}-${i}`} style={{ borderTop: '1px solid #e5e7eb' }}>
                <td style={{ padding: 10 }}>{f.nombre_usuario}</td>
                <td style={{ padding: 10 }}>{f.periodo}</td>
                <td style={{ padding: 10, textAlign: 'right' }}>{f.altas_prospecto}</td>
                <td style={{ padding: 10, textAlign: 'right' }}>{f.altas_infonavit}</td>
              </tr>
            ))}
          </tbody>
          {!loading && filas.length > 0 && (
            <tfoot>
              <tr style={{ borderTop: '2px solid #d1d5db', fontWeight: 'bold', background: '#f9fafb' }}>
                <td style={{ padding: 10 }} colSpan={2}>Total</td>
                <td style={{ padding: 10, textAlign: 'right' }}>{totales.altas_prospecto}</td>
                <td style={{ padding: 10, textAlign: 'right' }}>{totales.altas_infonavit}</td>
              </tr>
            </tfoot>
          )}
        </table>
      </div>
    </div>
  );
}
